import { AEO_DATA, quoted as Q } from '~/marketing/marketingData';

/* The FAQ, as one array.

   **This file is the whole correctness argument for the page.** `FAQPage`
   structured data must describe question-and-answer content that is visible on
   the page, so the markup and the prose have to come from one place or the
   markup is a claim about content that is not there. `Faq.jsx` renders this and
   `Seo` serialises the same objects — there is no second copy to keep in step.

   **Most answers are not written here.** Where a sanctioned Class 2 string
   already answers the question, the answer *is* that string, referenced from
   `quoted` rather than retyped — retyping it would be the second copy that
   `copy-rules.md` exists to prevent, and `copy_rules_test` only guards the
   strings in `quoted` against the file, not any prose that paraphrases them.

   `a` is an array of paragraphs. The rendered page prints one `<p>` each and the
   structured data joins them, so a reader and a crawler get the same words in
   the same order. */

const { total, commercial } = AEO_DATA.questions;
const [ starter, growth, agency ] = AEO_DATA.plans;
const usd = (n) => '$' + n.toLocaleString('en-US');

export const faq = [
  {
    q: 'Can I just ask ChatGPT myself?',
    a: [
      'You can, and it is worth doing once.',
      Q.variability,
    ],
  },
  {
    q: 'Is this the same as what I would see asking directly?',
    // A direct answer first, 2026-10-26; the sanctioned paragraph explains it.
    a: [ 'Close, but not identical.', Q.measurement_basis ],
  },
  {
    q: 'Which AI assistants do you measure?',
    a: [ Q.engine_coverage, Q.google_method, Q.google_none ],
  },
  {
    q: 'How often do you check?',
    // The answer first — every week, three times a question — then why not daily.
    a: [ Q.sampling, Q.weekly_not_daily ],
  },
  {
    q: 'What does “named” mean, and what is the number out of?',
    a: [ Q.questions_basis(total, commercial), Q.denominator_basis ],
  },
  {
    q: 'How do you know a mention is my business?',
    a: [ Q.name_matching ],
  },
  {
    q: 'Do I need a website?',
    a: [ 'No.', Q.no_website, Q.citation_measurement ],
  },
  {
    q: 'Can I choose the questions?',
    a: [
      `We write them for you. Each business gets ${total} questions for its industry, covering the four ways customers search — who to call, who is reputable, how options compare, and what things cost — with your city substituted in.`,
      'The set stays the same from week to week, which is what makes the trend readable: the same questions every time, so a change that holds across several readings reflects a change in how AI assistants describe you.',
      'If one doesn’t fit your business, tell us, and it helps us improve the questions for your industry.',
    ],
  },
  {
    q: 'Can I track more than one business or location?',
    a: [
      `Yes — the number of businesses you track is the only thing the price depends on. A second location and a second client cost the same to track, so a three-location company and a three-client marketing agency buy the same plan. Starter covers ${starter.count} ${starter.unit}, Growth ${growth.count} and Agency ${agency.count}.`,
      'Each business is tracked on its own questions, in its own city, because a different city is a different market.',
    ],
  },
  {
    q: 'What does it cost, and do I need a credit card to try it?',
    a: [
      `${usd(starter.monthly)}, ${usd(growth.monthly)} or ${usd(agency.monthly)} a month for ${starter.count}, ${growth.count} or ${agency.count} businesses. Every plan runs all five AI assistants on the same schedule. The trial runs ${AEO_DATA.trial.days} days and takes no credit card — when it ends, you choose whether to start a plan.`,
      Q.annual_before_purchase,
    ],
  },
  {
    q: 'What happens if I cancel?',
    a: [ Q.cancelling, Q.retention ],
  },
];
